interface SaveReplayRequest {
  sessionId: string;
  sessionUrl: string;
}

export const saveReplay = (request: SaveReplayRequest) => {
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

    fetch(`${apiUrl}/replay/save`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
  } catch (error) {
    console.error("Error saving replay:", error);
  }
};

interface InsertGASessionRequest {
  clickId?: string;
  gaClientId?: string;
  gaSessionId?: string;
  source?: string;
  sourceInfoId?: string;
  sourceSessionId?: string;
  visitorId?: string;
  fbp?: string;
  fbc?: string;
}

interface InsertGASessionResponse {
  success: boolean;
}

export const insertGASession = async (
  sessionData: InsertGASessionRequest,
): Promise<InsertGASessionResponse> => {
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

    const response = await fetch(`${apiUrl}/google-analytics/insert-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(sessionData),
    });

    if (!response.ok) {
      throw new Error("Failed to insert GA session");
    }

    return response.json();
  } catch (error) {
    console.error("Error inserting GA session:", error);

    return { success: false };
  }
};

export interface ICreateUserSessionRequest {
  sessionId: string;
  userAgent: string;
  sourceInfoId?: string;
  visitorId?: string | null;
  customerId?: string | null;
  optimizelySessionId?: string | null;
}

export const createUserSession = async (data: ICreateUserSessionRequest) => {
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

    await fetch(`${apiUrl}/tracking/session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  } catch (error) {
    console.error("Error creating user session:", error);
  }
};

export interface ITrackUserProgressRequest {
  sessionId: string;
  urlVisited: string;
  start?: string;
  completed?: string;
  sourceInfoId?: string;
  optimizelySessionId?: string | null;
}

export const insertUserProgress = async (data: ITrackUserProgressRequest) => {
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

    await fetch(`${apiUrl}/tracking/progress`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  } catch (error) {
    console.error("Error tracking user progress:", error);
  }
};

export const getRelatedArticles = async (category: string, tags: string[]) => {
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

    const response = await fetch(`${apiUrl}/sanity/related-articles`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ category, tags }),
    });

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching related articles:", error);
    return [];
  }
};

export const getRelatedArticlesBySlug = async (slug: string) => {
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

    const response = await fetch(`${apiUrl}/sanity/related-articles-by-slug`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ slug }),
    });

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching related articles by slug:", error);
    return { tagCategoryArticles: [], categoryArticles: [] };
  }
};

export const getCalculatorArticleSlugs = async (): Promise<string[]> => {
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

    const response = await fetch(`${apiUrl}/sanity/calculator-article-slugs`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching calculator article slugs:", error);
    return [];
  }
};

export interface ICustomerSavings {
  name: string;
  amount: number;
  location: string;
}

export interface ISiteRating {
  average: number;
  totalReviews: number;
}

export interface IStatisticsResponse {
  applicationsStarted: number;
  applicationsApproved: number;
  customerSavings: ICustomerSavings;
  lastLoanClosed: string;
  siteRating: ISiteRating;
}

export const getStatistics = async (): Promise<IStatisticsResponse | null> => {
  try {
    const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

    const response = await fetch(`${apiUrl}/statistics`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching statistics:", error);
    return null;
  }
};
